import { useController, useFormContext } from 'react-hook-form';
import useCustomFields from '../../custom_categories/hooks/useCustomFields';
import VisibleDialog from './VisibleDialog';
import type { FormValue } from '../searchSchema';
import Input from '@/components/Input';
import Checkbox from '@/components/Checkbox';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useCandidateProfileTags from '../hooks/useCandidateProfileTags';
import useFetchIam from '../../api/useFetchIam';
import Select from '@/components/Select';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { MdSettings, MdArrowDropDown, MdOutlineClose, MdAutorenew } from 'react-icons/md';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { SharedCustomField } from '@/types/serializers';
import MultiSelect from '@/components/MultiSelect';
import MultiSelectManageLink from '@/components/MultiSelectManageLink';
import CustomTagMatchMenu from '@/components/CustomTagMatchMenu';
import 'react-datepicker/dist/react-datepicker.css';
import CandidatesDateField from './CandidatesDateField';
import useCustomFieldSearch, { type ExtendedCustomField } from '../hooks/useCustomFieldSearch';
import { Link } from '@tanstack/react-router';
import type { CustomFieldQuery } from '../api/useFetchCandidateProfiles';
import CandidatesAddDropdown from './CandidatesAddDropdown';

/** 「未設定」を表す担当者ID */
const UNASSIGNED_USER_ID = -1;

interface RemoveButtonProps {
  onClick: () => void;
}

function RemoveButton({ onClick }: RemoveButtonProps) {
  return (
    <button type="button" className="flex size-6 items-center justify-center" onClick={onClick}>
      <MdOutlineClose className="size-6 text-[#6D6D6D]" />
    </button>
  );
}

const orderItems = [
  { value: 'updated_at', label: '最終更新順' },
  { value: 'id', label: '登録日順' },
  { value: 'job_post_endpoint_created_at', label: '求人提案順' },
  { value: 'job_change_process_updated_at', label: '最終進捗更新順' },
];

interface Props {
  sort: string;
  totalCount: number;
  screenType: 'list' | 'board';
  customFields: SharedCustomField[];
  isLoading: boolean;
  onSubmit: (data: FormValue) => void;
  onChangeSort: (sort: string) => void;
  setScreenType: (value: 'list' | 'board') => void;
  initialCustomFields?: CustomFieldQuery[];
}

export default function SearchForm({
  customFields,
  onSubmit,
  sort,
  onChangeSort,
  totalCount,
  screenType,
  setScreenType,
  isLoading,
  initialCustomFields,
}: Props) {
  const { register, control, watch, reset } = useFormContext<FormValue>();
  const { data, isSuccess } = useCandidateProfileTags();
  const { data: activeUsersData } = useActiveUsers();
  const { data: customFieldData } = useCustomFields();
  const { data: iamData } = useFetchIam();
  const customTagIds = watch('customTagIds');
  const customTagIdsController = useController({ name: 'customTagIds', control });
  const customTagMatchController = useController({ name: 'customTagMatch', control });
  const organizationUserIdsController = useController({
    name: 'organizationUserIds',
    control,
    defaultValue: [],
  });
  const includeFinishedController = useController({
    name: 'includeFinished',
    control,
    defaultValue: false,
  });
  const [isSearchPopoverOpen, setIsSearchPopoverOpen] = useState(false);
  const [selectFieldValues, setSelectFieldValues] = useState<Record<number, number>>({});

  // 固定のソート項目に加え、その組織の date/datetime 型カスタムフィールドを動的に列挙する（Issue #3040）。
  // 値は `custom_field:<id>` 形式で、そのまま API に送られ ReadModel::CandidateProfile#sort_order_by で解釈される。
  const sortItems = useMemo(() => {
    const dateFieldItems = (customFieldData?.customFields ?? [])
      .filter((field) => field.fieldType === 'date' || field.fieldType === 'datetime')
      .map((field) => ({ value: `custom_field:${field.id}`, label: `${field.name}順` }));
    return [...orderItems, ...dateFieldItems];
  }, [customFieldData]);

  // CustomField検索機能をカスタムフックに移譲
  const customFieldSearch = useCustomFieldSearch({
    onSubmit,
    watch,
    initialCustomFields,
    availableCustomFields: customFields.map((field) => ({
      id: field.id,
      name: field.name,
      unitName: field.unit_name,
      orderNumber: field.order_number,
      fieldType: field.field_type,
      custom_field_options: (field as SharedCustomField).custom_field_options.map((opt) => ({
        id: opt.id,
        value: opt.value,
      })),
    })),
  });
  const formValues = watch();

  const handleSelectChange = useCallback(
    (fieldId: number, value: number) => {
      setSelectFieldValues((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
      customFieldSearch.updateFieldValue(fieldId, value);
    },
    [customFieldSearch]
  );

  // customFieldsが復元されたときに、single_selectの値をselectFieldValuesに反映
  useEffect(() => {
    const customFieldsState = customFieldSearch.customFields;
    const newSelectFieldValues: Record<number, number> = {};

    Object.values(customFieldsState).forEach((field) => {
      if (field.fieldType === 'single_select' && typeof field.value === 'number') {
        newSelectFieldValues[field.fieldId] = field.value;
      }
    });

    // 既存の値とマージ
    setSelectFieldValues((prev) => {
      const merged = { ...prev, ...newSelectFieldValues };
      // 値が変更された場合のみ更新
      if (JSON.stringify(merged) !== JSON.stringify(prev)) {
        return merged;
      }
      return prev;
    });
  }, [customFieldSearch.customFields]);

  const hasFormValues = useCallback(() => {
    const hasBasicFormValues =
      (formValues.keyword && formValues.keyword.trim() !== '') ||
      (formValues.organizationUserIds && formValues.organizationUserIds.length > 0) ||
      (customTagIds && customTagIds.length > 0);

    const hasCustomFieldValues = customFieldSearch.hasCustomFieldValues();

    return hasBasicFormValues || hasCustomFieldValues;
  }, [formValues, customTagIds, customFieldSearch]);

  const submitSearch = useCallback(() => {
    const data = watch();
    const customFields = customFieldSearch.collectCustomFieldValues();
    const extendedData = {
      ...data,
      customFields,
    };
    onSubmit(extendedData);
  }, [watch, onSubmit, customFieldSearch]);

  useEffect(() => {
    const subscription = watch(() => {
      submitSearch();
    });
    return () => subscription.unsubscribe();
  }, [watch, submitSearch]);

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className="flex flex-col gap-5 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-sm">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold">{isLoading ? '---' : totalCount}</span>
              <span className="text-sm">件</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <CandidatesAddDropdown />
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => setScreenType(screenType === 'list' ? 'board' : 'list')}
            >
              <MdAutorenew className="size-icon-xs" />
              {screenType === 'list' ? 'ボードビュー' : 'リストビュー'}
            </Button>

            {iamData?.current_user?.is_admin && (
              <Button color="secondary" size="md" asChild iconOnly>
                <Link to="/agent/customize" search={{ type: 'candidate_profile' as const }}>
                  <MdSettings className="size-icon-xs" />
                </Link>
              </Button>
            )}
          </div>
        </div>
        <div className="rounded bg-white p-4 shadow-[0px_0px_2px_0px_rgba(0,_0,_0_0.20)]">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex max-w-[422px] min-w-[240px] flex-1 basis-[422px] flex-col gap-2">
              <label className="m-0 text-sm font-bold">フリーワード</label>
              <Input
                search
                className="w-full"
                autoComplete="off"
                placeholder="フリーワードで絞り込む"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                {...register('keyword')}
              />
            </div>
            {activeUsersData && (
              <div className="flex w-[236px] shrink-0 flex-col gap-2">
                <label className="m-0 text-sm font-bold">担当者</label>
                <MultiSelect
                  options={[
                    { id: UNASSIGNED_USER_ID, name: '未設定' },
                    ...activeUsersData.activeUsers.map((user) => ({
                      id: user.id,
                      name: user.name,
                    })),
                  ]}
                  value={organizationUserIdsController.field.value ?? []}
                  onChange={(value) => organizationUserIdsController.field.onChange(value)}
                  placeholder="担当者を選択"
                  matchTriggerWidth
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="flex w-[230px] shrink-0 flex-col gap-2">
              <label className="m-0 text-sm font-bold">年齢</label>
              <div className="flex items-center gap-2">
                <div className="w-[100px]">
                  <Input
                    className="w-full"
                    autoComplete="off"
                    placeholder=""
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    {...register('age.min', {
                      setValueAs: (value) => (value === '' ? null : Number(value)),
                    })}
                  />
                </div>
                <span className="text-sm font-bold">〜</span>
                <div className="w-[100px]">
                  <Input
                    className="w-full"
                    autoComplete="off"
                    placeholder=""
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    {...register('age.max', {
                      setValueAs: (value) => (value === '' ? null : Number(value)),
                    })}
                  />
                </div>
              </div>
            </div>
            <div className="flex min-w-[280px] flex-1 gap-4">
              <div className="flex w-full max-w-[672px] min-w-0 flex-col gap-xs">
                <label className="m-0 text-heading-xxs">タグ</label>
                {isSuccess && data?.tags && (
                  <MultiSelect
                    options={data.tags}
                    value={customTagIds || []}
                    onChange={(value) => customTagIdsController.field.onChange(value)}
                    matchTriggerWidth
                    variant="list"
                    header={
                      <CustomTagMatchMenu
                        value={customTagMatchController.field.value}
                        onChange={customTagMatchController.field.onChange}
                      />
                    }
                    footer={
                      <MultiSelectManageLink to="/agent/candidate_tags">
                        タグを管理
                      </MultiSelectManageLink>
                    }
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-6">
              <Popover
                open={isSearchPopoverOpen}
                onOpenChange={setIsSearchPopoverOpen}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <Button
                    color="secondary"
                    size="md"
                    active={isSearchPopoverOpen}
                    className="w-[146px]"
                  >
                    検索条件を追加
                    <div className="min-h-md min-w-md">
                      <MdArrowDropDown
                        className={`size-icon-xs transition-transform duration-200 ${isSearchPopoverOpen ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="rounded-sm bg-white p-0">
                  <div className="hidden-scrollbar flex max-h-[320px] flex-col overflow-hidden overflow-y-auto">
                    {customFields.map((c) => {
                      return (
                        <button
                          key={c.id}
                          type="button"
                          className="min-h-xxl px-3 text-left text-sm hover:bg-[#F6F6F6]"
                          onClick={() => {
                            const customField: ExtendedCustomField = {
                              id: c.id,
                              name: c.name,
                              unitName: null,
                              orderNumber: 0,
                              fieldType:
                                (c as unknown as { field_type?: string }).field_type ||
                                'short_text',
                              custom_field_options: c.custom_field_options || [],
                            };
                            customFieldSearch.addCustomField(customField);
                            setIsSearchPopoverOpen(false);
                          }}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              <Checkbox
                checked={includeFinishedController.field.value ?? false}
                onChange={(checked) => includeFinishedController.field.onChange(checked)}
              >
                <span className="text-xs">終了した候補者を含める</span>
              </Checkbox>
              {hasFormValues() && (
                <Button
                  color="tertiary"
                  size="md"
                  className="ml-auto"
                  onClick={() => {
                    reset();
                    customFieldSearch.clearAllCustomFields();
                  }}
                >
                  <MdOutlineClose className="size-icon-xs text-primary" />
                  <p className="text-sm font-bold text-primary">検索条件をクリア</p>
                </Button>
              )}
            </div>
            {customFieldSearch.getSelectedFields().length > 0 && (
              <div>
                <div className="flex flex-wrap gap-4">
                  {customFieldSearch.getSelectedFields().map((field) => (
                    <div key={field.fieldId} className="flex flex-col gap-4">
                      <label className="m-0 text-sm font-bold">{field.fieldName}</label>
                      <div className="relative w-full">
                        {field.fieldType === 'short_text' || field.fieldType === 'long_text' ? (
                          <div className="flex items-center gap-1">
                            <div className="w-[300px]">
                              <Input
                                className="w-full"
                                autoComplete="off"
                                placeholder=""
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                  }
                                }}
                                value={(field.value as string) || ''}
                                onChange={(e) =>
                                  customFieldSearch.updateFieldValue(field.fieldId, e.target.value)
                                }
                              />
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        ) : field.fieldType === 'single_select' ? (
                          <div className="flex items-center gap-1">
                            <div className="w-[187px]">
                              <Select
                                className="w-full"
                                items={(field.options || []).map((option) => ({
                                  value: String(option.id),
                                  label: option.value,
                                }))}
                                defaultLabel={`${field.fieldName}を選択`}
                                value={String(selectFieldValues[field.fieldId] || '')}
                                onValueChange={(value) => {
                                  const numericValue = value ? Number(value) : 0;
                                  handleSelectChange(field.fieldId, numericValue);
                                }}
                              />
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        ) : field.fieldType === 'multiple_select' ? (
                          <div className="flex items-center gap-1">
                            <div className="w-[187px]">
                              <MultiSelect
                                options={(field.options || []).map((option) => ({
                                  id: option.id,
                                  name: option.value,
                                }))}
                                value={(field.value as number[]) || []}
                                onChange={(value) =>
                                  customFieldSearch.updateFieldValue(field.fieldId, value)
                                }
                                placeholder={`${field.fieldName}を選択`}
                              />
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        ) : field.fieldType === 'number' ? (
                          <div className="flex items-center gap-1">
                            <div className="flex items-center gap-2">
                              <div className="w-[131px]">
                                <Input
                                  className="w-full"
                                  autoComplete="off"
                                  placeholder=""
                                  type="number"
                                  value={
                                    typeof field.value === 'object' &&
                                    field.value &&
                                    'min' in field.value
                                      ? (field.value.min ?? '')
                                      : ''
                                  }
                                  onChange={(e) => {
                                    const currentValue =
                                      typeof field.value === 'object' &&
                                      field.value &&
                                      'min' in field.value &&
                                      'max' in field.value
                                        ? field.value
                                        : {
                                            min: null,
                                            max: null,
                                          };
                                    const newValue = e.target.value ? Number(e.target.value) : null;
                                    customFieldSearch.updateFieldValue(field.fieldId, {
                                      ...currentValue,
                                      min: newValue,
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                              <span className="text-sm font-bold">〜</span>
                              <div className="w-[131px]">
                                <Input
                                  className="w-full"
                                  autoComplete="off"
                                  placeholder=""
                                  type="number"
                                  value={
                                    typeof field.value === 'object' &&
                                    field.value &&
                                    'max' in field.value
                                      ? (field.value.max ?? '')
                                      : ''
                                  }
                                  onChange={(e) => {
                                    const currentValue =
                                      typeof field.value === 'object' &&
                                      field.value &&
                                      'min' in field.value &&
                                      'max' in field.value
                                        ? field.value
                                        : {
                                            min: null,
                                            max: null,
                                          };
                                    const newValue = e.target.value ? Number(e.target.value) : null;
                                    customFieldSearch.updateFieldValue(field.fieldId, {
                                      ...currentValue,
                                      max: newValue,
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        ) : field.fieldType === 'date' ? (
                          <div className="flex items-center gap-1">
                            <div className="w-[300px]">
                              <CandidatesDateField
                                fieldId={field.fieldId}
                                value={
                                  typeof field.value === 'object' &&
                                  field.value &&
                                  'min' in field.value &&
                                  'max' in field.value
                                    ? {
                                        min:
                                          typeof field.value.min === 'string'
                                            ? field.value.min
                                            : field.value.min?.toString() || null,
                                        max:
                                          typeof field.value.max === 'string'
                                            ? field.value.max
                                            : field.value.max?.toString() || null,
                                      }
                                    : { min: null, max: null }
                                }
                                onDateChange={(fieldId, min, max) => {
                                  customFieldSearch.updateFieldValue(fieldId, { min, max });
                                }}
                              />
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="w-[300px]">
                              <Input className="w-full" autoComplete="off" placeholder="" />
                            </div>
                            <RemoveButton
                              onClick={() => customFieldSearch.removeCustomField(field.fieldId)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-end justify-between gap-4">
          <div className="flex grow items-end gap-4">
            <div className="flex w-[196px] shrink-0 flex-col gap-2">
              <Select
                className="w-full"
                onValueChange={(value) => onChangeSort(value)}
                value={sort}
                items={sortItems}
              />
            </div>
          </div>
          {customFieldData && <VisibleDialog customFields={customFieldData.customFields} />}
        </div>
      </div>
    </form>
  );
}
